<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { computed } from 'vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useMapGetter } from 'dashboard/composables/store';
import { useConversationSidebars } from 'dashboard/composables/useConversationSidebars';
import { clampSidebarWidthPercent } from 'dashboard/helper/conversationSidebarSizing';

const { updateUISettings } = useUISettings();

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showCopilotTab = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.CAPTAIN)
);

const { uiSettings } = useUISettings();
const {
  isContactSidebarOpen,
  isOrderSidebarOpen,
  openContactSidebar,
  closeContactSidebar,
  closeOrderSidebar,
} = useConversationSidebars();
const isCopilotPanelOpen = computed(
  () => uiSettings.value.is_copilot_panel_open
);

const handleContactSidebarToggle = () => {
  if (isContactSidebarOpen.value) {
    closeContactSidebar();
    return;
  }

  const currentContactWidthPercent = Number(
    uiSettings.value.conversation_contact_sidebar_width || 30
  );
  const currentOrderWidthPercent = Number(
    uiSettings.value.conversation_order_sidebar_width || 30
  );

  updateUISettings({
    conversation_contact_sidebar_width: clampSidebarWidthPercent({
      nextWidthPercent: currentContactWidthPercent,
      otherSidebarOpen: isOrderSidebarOpen.value,
      otherSidebarWidthPercent: currentOrderWidthPercent,
    }),
    is_copilot_panel_open: false,
  });

  closeOrderSidebar();
  openContactSidebar();
};

const handleCopilotSidebarToggle = () => {
  updateUISettings({
    is_copilot_panel_open: true,
  });
  closeContactSidebar();
  closeOrderSidebar();
};
</script>

<template>
  <ButtonGroup
    class="flex flex-col justify-center items-center absolute top-36 xl:top-24 ltr:right-2 rtl:left-2 bg-n-solid-2/90 backdrop-blur-lg border border-n-weak/50 rounded-full gap-1.5 p-1.5 shadow-sm transition-shadow duration-200 hover:shadow"
  >
    <Button
      v-tooltip.top="$t('CONVERSATION.SIDEBAR.CONTACT')"
      ghost
      slate
      sm
      class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:!brightness-105 active:duration-75"
      :class="{
        'bg-n-alpha-2 active:shadow-sm': isContactSidebarOpen,
      }"
      icon="i-lucide-user-round"
      @click="handleContactSidebarToggle"
    />
    <Button
      v-if="showCopilotTab"
      v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.COPILOT')"
      ghost
      slate
      sm
      class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:duration-75"
      :class="{
        'bg-n-alpha-2 !text-n-iris-9 active:!brightness-105 active:shadow-sm':
          isCopilotPanelOpen,
      }"
      icon="i-woot-captain"
      @click="handleCopilotSidebarToggle"
    />
  </ButtonGroup>
</template>
